import { useState, useCallback } from "react";
import { writeClipboardRich } from "../lib/clipboard";

/**
 * Copy text to clipboard with a 2-second check-mark confirmation state.
 * Falls back to a textarea workaround when the Clipboard API is blocked (e.g. inside iframes).
 *
 * Returns [showCheck, copy] where `copy(text, html?)` copies and sets `showCheck = true` for 2 s.
 * When `html` is given, the clipboard carries both flavors: rich paste targets
 * (Apple Mail, Outlook) take the formatted HTML, plain targets take the text.
 */
export function useCopyWithCheck(): [boolean, (text: string, html?: string) => void] {
    const [showCheck, setShowCheck] = useState(false);

    const copy = useCallback((text: string, html?: string) => {
        const onSuccess = () => {
            setShowCheck(true);
            setTimeout(() => setShowCheck(false), 2000);
        };

        const fallback = () => {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            onSuccess();
        };

        if (html !== undefined) {
            void writeClipboardRich({ html, text }).then((ok) => {
                if (ok) onSuccess();
                else fallback();
            });
            return;
        }

        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(onSuccess, fallback);
        } else {
            fallback();
        }
    }, []);

    return [showCheck, copy];
}
